import { redirect } from 'react-router-dom';
import { useContract } from '@/core/api';
import type { useAuth } from '@/core/auth';
import { AuthLayout, GmnLayout } from '@/core/layouts';
import { CustomerLayoutWrapper } from '@/core/layouts/customer-layout-wrapper';
import { ManageCustomer as CustomerSelfService } from '@/features/customer/customer';
import { CustomersOverview, ManageCustomer as ManageCustomerAdmin } from '@/features/gmn/customers';
import { ManageContractMetadata } from '@/features/gmn/metadata';
import { ManageObjectCategory, ObjectCategoriesOverview } from '@/features/gmn/object-categories';
import { MyAccount } from '@/features/shared/account';
import {
  AcceptInvite,
  Login,
  MicrosoftCallback,
  RequestPasswordReset,
  ResetPassword,
} from '@/features/shared/auth';
import { ContractDetails, ContractsOverview } from '@/features/shared/contracts';
import { Notifications } from '@/features/shared/notifications';
import { ManageObject, ObjectsOverview } from '@/features/shared/objects';
import { UserManagement } from '@/features/shared/users';
import type { CustomRouteObjectUnion } from '@/shared/types';

import { privateRoute, publicRoute, unknownRoute } from './guards';

export const createRoutes = (user: ReturnType<typeof useAuth>['user']) =>
  [
    {
      path: '*',
      loader: () => unknownRoute(user),
      element: <div>Unknown</div>,
    },
    {
      path: '/auth',
      element: <AuthLayout />,
      loader: () => publicRoute(user),
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'microsoft/callback',
          element: <MicrosoftCallback />,
        },
        {
          path: 'forgot-password',
          element: <RequestPasswordReset />,
        },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
        {
          path: 'accept-invite',
          element: <AcceptInvite />,
        },
      ],
    },
    {
      path: '/c',
      loader: () => privateRoute(['customer-admin'])(user),
      element: <CustomerLayoutWrapper />,
      children: [
        {
          index: true,
          loader: () => redirect('/c/my-customer/customer'),
        },

        {
          path: 'my-objects',
          handle: {
            breadcrumb: () => 'objects.myObjects',
          },
          children: [
            {
              index: true,
              element: <ObjectsOverview filterForCurrentUserCustomer={true} />,
            },
            {
              path: ':id',
              element: <ManageObject />,
              handle: {
                breadcrumb: () => 'objects.editObject',
              },
              children: [
                {
                  index: true,
                  loader: () => redirect('details'),
                },
                {
                  path: 'details',
                  element: <ManageObject />,
                  handle: {
                    tab: 'details',
                  },
                },
                {
                  path: 'activity',
                  element: <ManageObject />,
                  handle: {
                    tab: 'activity',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'my-customer/customer',
          element: <CustomerSelfService />,
          handle: {
            breadcrumb: () => 'customers.customer',
          },
        },
        {
          path: 'my-customer',
          loader: () => redirect('/c/my-customer/customer'),
        },
        {
          path: 'my-customer/users',
          loader: () => redirect('/c/my-customer/customer'),
        },
        {
          path: 'my-account',
          element: <MyAccount />,
          handle: {
            breadcrumb: () => 'auth.myAccount',
          },
        },
        {
          path: 'notifications',
          handle: {
            breadcrumb: () => 'common.notifications',
          },
          children: [
            {
              index: true,
              loader: () => redirect('unread'),
            },
            {
              path: 'unread',
              element: <Notifications />,
            },
            {
              path: 'read',
              element: <Notifications />,
            },
            {
              path: 'archived',
              element: <Notifications />,
            },
          ],
        },
      ],
    },
    {
      path: '/gmn',
      loader: () => privateRoute(['gmn-admin'])(user),
      element: <GmnLayout />,
      children: [
        {
          index: true,
          loader: () => redirect('/gmn/contract-management/contracts'),
        },

        {
          path: 'contract-management',
          children: [
            {
              path: 'contracts',
              handle: {
                breadcrumb: () => 'contracts.contracts',
              },
              children: [
                {
                  index: true,
                  element: <ContractsOverview />,
                },
                {
                  path: ':id',
                  element: <ContractDetails />,
                  handle: {
                    breadcrumb: (match) => ({
                      query: {
                        queryFn: useContract,
                        id: match.params.id!,
                        dataKey: 'title',
                      },
                    }),
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('general'),
                    },
                    {
                      path: 'general',
                      element: <ContractDetails />,
                      handle: {
                        tab: 'general',
                      },
                    },
                    {
                      path: 'activity',
                      element: <ContractDetails />,
                      handle: {
                        tab: 'activity',
                      },
                    },
                  ],
                },
                {
                  path: 'new',
                  element: <ContractDetails />,
                  handle: {
                    breadcrumb: () => 'contracts.newContract',
                  },
                },
              ],
            },
            {
              path: 'objects',
              handle: {
                breadcrumb: () => 'objects.objects',
              },
              children: [
                {
                  index: true,
                  element: <ObjectsOverview />,
                },
                {
                  path: 'new',
                  element: <ManageObject />,
                  handle: {
                    breadcrumb: () => 'objects.newObject',
                  },
                },
                {
                  path: ':id',
                  element: <ManageObject />,
                  handle: {
                    breadcrumb: () => 'objects.editObject',
                  },
                  children: [
                    {
                      index: true,
                      loader: () => redirect('details'),
                    },
                    {
                      path: 'details',
                      element: <ManageObject />,
                      handle: {
                        tab: 'details',
                      },
                    },
                    {
                      path: 'activity',
                      element: <ManageObject />,
                      handle: {
                        tab: 'activity',
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: 'metadata',
              element: <ManageContractMetadata />,
              handle: {
                breadcrumb: () => 'contracts.metadata',
              },
            },
          ],
        },

        {
          path: 'customer-management',
          children: [
            {
              path: 'customers',
              handle: {
                breadcrumb: () => 'customers.customers',
              },
              children: [
                {
                  index: true,
                  element: <CustomersOverview />,
                },
                {
                  path: 'new',
                  element: <ManageCustomerAdmin />,
                  handle: {
                    breadcrumb: () => 'customers.newCustomer',
                  },
                },
                {
                  path: ':id',
                  element: <ManageCustomerAdmin />,
                  handle: {
                    breadcrumb: () => 'customers.editCustomer',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'settings',
          children: [
            {
              path: 'users',
              element: <UserManagement allowedRoles={['gmn-admin']} />,
              handle: {
                breadcrumb: () => 'users.users',
              },
            },
            {
              path: 'object-categories',
              handle: {
                breadcrumb: () => 'objectCategories.objectCategories',
              },
              children: [
                {
                  index: true,
                  element: <ObjectCategoriesOverview />,
                },
                {
                  path: 'new',
                  element: <ManageObjectCategory />,
                  handle: {
                    breadcrumb: () => 'objectCategories.newCategory',
                  },
                },
                {
                  path: ':id',
                  element: <ManageObjectCategory />,
                  handle: {
                    breadcrumb: () => 'objectCategories.editCategory',
                  },
                },
              ],
            },
            {
              path: 'metadata',
              element: <ManageContractMetadata />,
              handle: {
                breadcrumb: () => 'metadata.contractMetadata',
              },
            },
          ],
        },

        {
          path: 'my-account',
          element: <MyAccount />,
          handle: {
            breadcrumb: () => 'auth.myAccount',
          },
        },
        {
          path: 'notifications',
          handle: {
            breadcrumb: () => 'common.notifications',
          },
          children: [
            {
              index: true,
              loader: () => redirect('unread'),
            },
            {
              path: 'unread',
              element: <Notifications />,
            },
            {
              path: 'read',
              element: <Notifications />,
            },
            {
              path: 'archived',
              element: <Notifications />,
            },
          ],
        },
      ],
    },
  ] as CustomRouteObjectUnion[];
